import {
  type AgentScope,
  type AgenticSettingsEntityFieldsFragment,
  AgenticSettingsEntitySourceType,
  type AgenticSettingsEntityType,
  type ContextFileType,
} from "src/graphql/types";

export type ContextEntity = AgenticSettingsEntityFieldsFragment;

export type MarkdownContent = Extract<
  ContextEntity["content"],
  { __typename: "AgenticSettingsMarkdownContent" }
>;

export type FileContent = Extract<
  ContextEntity["content"],
  { __typename: "AgenticSettingsFileContent" }
>;

export type CopyExamplesContent = Extract<
  ContextEntity["content"],
  { __typename: "AgenticSettingsCopyExamplesContent" }
>;

export const isMarkdownEntity = (
  entity: ContextEntity,
): entity is ContextEntity & { content: MarkdownContent } =>
  entity.content.__typename === "AgenticSettingsMarkdownContent";

export const isFileEntity = (
  entity: ContextEntity,
): entity is ContextEntity & { content: FileContent } =>
  entity.content.__typename === "AgenticSettingsFileContent";

export const isCopyExamplesEntity = (
  entity: ContextEntity,
): entity is ContextEntity & { content: CopyExamplesContent } =>
  entity.content.__typename === "AgenticSettingsCopyExamplesContent";

export const isManagedEntity = (entity: ContextEntity): boolean =>
  entity.isManaged === true;

/**
 * Produced from another entity's content rather than authored: the page images
 * and extracted text an uploaded PDF is broken into. Nobody curates these, so
 * the reference surfaces leave them out; an agent still reaches them through
 * the document that cites them.
 */
export const isDerivedEntity = (entity: ContextEntity): boolean =>
  entity.sourceType === AgenticSettingsEntitySourceType.Derived;

export type ContextEntityType = AgenticSettingsEntityType;

/**
 * A file the user picked but hasn't saved yet. The raw File is kept so the
 * bytes can be PUT to a presigned URL at save time (see
 * useUploadContextFile) — files are no longer base64-encoded into the
 * GraphQL mutation body.
 */
export type StagedFile = {
  filename: string;
  filetype: string;
  filesize: number;
  file: File;
};

export type ContextEntityFormValues = {
  name: string;
  description: string;
  isActive: boolean;
  /** Hidden from listings; reaches an agent only through documents that cite it. */
  isReferenceOnly: boolean;
  /**
   * Classification used to decide when the file is loaded. Null means
   * unclassified (a general context doc loaded on demand).
   */
  contextType: ContextFileType | null;
  type: ContextEntityType;
  markdown: string;
  existingFile: {
    filename: string;
    filetype: string;
    filesize: number;
    url: string;
  } | null;
  stagedFile: StagedFile | null;
  /**
   * Brand-profile assignments. Empty array means "applies to all brand
   * profiles" (no join rows). Only ids of non-deleted profiles in the
   * workspace.
   */
  brandProfileIds: string[];
  /**
   * Agents this context is scoped to. Empty array means "applies to all
   * agents" (the backend persists this as null/empty).
   */
  agentScopes: AgentScope[];
};
